<template>
  <div class="w-full">
    <label
      v-if="label"
      :for="inputId"
      class="text-navy-700 mb-1.5 block text-sm font-medium"
    >
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>
    <input
      :id="inputId"
      ref="inputRef"
      v-bind="$attrs"
      v-model="model"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :required="required"
      :autocomplete="autocomplete"
      :class="inputClasses"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    />
    <p v-if="error" class="mt-1.5 text-sm text-red-600">
      {{ error }}
    </p>
    <p v-else-if="hint" class="text-navy-400 mt-1.5 text-sm">
      {{ hint }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useId } from "vue"

type InputType =
  | "text"
  | "email"
  | "password"
  | "number"
  | "tel"
  | "url"
  | "search"

interface Props {
  label?: string
  type?: InputType
  placeholder?: string
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  error?: string
  hint?: string
  autocomplete?: string
}

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  type: "text",
  placeholder: undefined,
  disabled: false,
  readonly: false,
  required: false,
  error: undefined,
  hint: undefined,
  autocomplete: undefined,
})

defineEmits<{
  blur: [event: FocusEvent]
  focus: [event: FocusEvent]
}>()

const model = defineModel<string | number>()
const inputRef = ref<HTMLInputElement | null>(null)
const inputId = useId()

const inputClasses = computed(() => [
  "block w-full rounded-md border px-3 py-2 text-navy-800 placeholder-navy-300 transition-colors duration-200",
  "focus:outline-none focus:ring-2 focus:ring-offset-0",
  "disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-navy-400",
  props.error
    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
    : "border-neutral-300 focus:border-green-500 focus:ring-green-500/20",
])

const focus = () => inputRef.value?.focus()
const blur = () => inputRef.value?.blur()

defineOptions({
  inheritAttrs: false,
})

defineExpose({ focus, blur, inputRef })
</script>
